import { useAtomValue } from 'jotai';
import { useEffect } from 'react';
import { ErrorBoundary as BaseErrorBoundary } from 'react-error-boundary';

import { useTranslate } from '@growtherapy/i18n';

import { EditIntentionModalConnected, IntentionSetModal } from '@grow-therapy-team/seedling-components';

import { SessionIntention__CreatedBy, SessionIntention__EditedBy } from '../../hooks/intentions/types';
import { useGetCurrentIntentionQuery } from '../../hooks/intentions/useGetCurrentIntentionQuery';
import { useUpdateSessionIntentionMutation } from '../../hooks/intentions/useUpdateSessionIntentionMutation';
import { useAppDependenciesContext } from '../../state';
import { useSendSessionIntentionEditedCallback } from '../messages/useSendSessionIntentionEditedCallback';
import { inSessionPatientInformationAtom } from '../state';

function ErrorBoundary({ children }: { children: React.ReactNode }) {
  return <BaseErrorBoundary FallbackComponent={() => null}>{children}</BaseErrorBoundary>;
}

export const IntentionSetModalWrapper = ({
  isOpen,
  onClose,
  clientName,
  initials,
  clientUserShortId,
  providerShortId,
  appointmentShortId,
  providerCanSetIntentions,
}: {
  isOpen: boolean;
  onClose: () => void;
  clientName: string;
  initials: string;
  clientUserShortId: string;
  providerShortId: string;
  appointmentShortId: string;
  providerCanSetIntentions: boolean;
}) => {
  const { logger, toast } = useAppDependenciesContext();
  const t = useTranslate();
  const { currentIntention, loading, refetch } = useGetCurrentIntentionQuery(
    clientUserShortId,
    providerShortId,
    appointmentShortId,
  );
  const [updateSessionIntention] = useUpdateSessionIntentionMutation();
  const sendSessionIntentionEdited = useSendSessionIntentionEditedCallback();
  const inSessionPatientInfo = useAtomValue(inSessionPatientInformationAtom);
  const intentionEditedAt = inSessionPatientInfo?.intentionEditedAt;

  useEffect(
    function refetchEditedIntention() {
      if (intentionEditedAt) {
        refetch();
      }
    },
    [intentionEditedAt, refetch],
  );

  if (loading || !currentIntention) {
    return <></>;
  }

  const intentionLastEditedByClient =
    currentIntention.editedBy && currentIntention.editedBy === SessionIntention__EditedBy.CLIENT;
  /**
   * Providers can edit the intention if:
   * - provider can set the intention
   * - the intention was created by the provider
   * - client hasn't edited it yet
   */
  const showEditIntentionModal =
    providerCanSetIntentions &&
    currentIntention.createdBy === SessionIntention__CreatedBy.PROVIDER &&
    !intentionLastEditedByClient;

  const handleUpdateIntention = async (intention: string) => {
    try {
      const result = await updateSessionIntention({
        variables: {
          sessionIntentionShortId: currentIntention.shortId,
          contentText: intention,
        },
      });

      const shortId = result.data?.updateSessionIntention?.sessionIntention?.shortId;
      if (shortId) {
        await sendSessionIntentionEdited(shortId);
        onClose();
      }
    } catch (error) {
      logger.error('Error editing session intention', { error });
      toast.error(t('telehealth-core:sessionIntention.couldNotEditIntention'));
    }
  };

  return (
    <ErrorBoundary>
      {showEditIntentionModal ? (
        <EditIntentionModalConnected
          appointmentShortId={appointmentShortId}
          sessionIntentionShortId={currentIntention.shortId}
          isOpen={isOpen}
          onClose={onClose}
          onSaveIntention={handleUpdateIntention}
          name={clientName}
          intention={currentIntention.contentText}
          initials={initials}
        />
      ) : (
        <IntentionSetModal
          isOpen={isOpen}
          onClose={onClose}
          name={clientName}
          initials={initials}
          intention={currentIntention.contentText}
        />
      )}
    </ErrorBoundary>
  );
};
